import { FilterFn, FilterFns } from '@tanstack/react-table';
import {
  getDayjsFromAny,
  getIsoDateFromFormattedDate,
  getIsoDateFromFormattedDateTime,
  MAX_TIMESTAMP,
  MIN_TIMESTAMP,
} from '../../utils/dateUtils';

/**
 * Wraps a filter function to always include rows marked as `isNew`.
 */
export const wrapNewCheckFilterFn = <TData>(
  filterFn: FilterFn<TData>,
): FilterFn<TData> => {
  const wrapped = (row: any, columnId: any, filterValue: any, addMeta: any) => {
    if ((row.original as { isNew?: boolean } | undefined)?.isNew) {
      return true;
    }
    return filterFn(row, columnId, filterValue, addMeta);
  };
  if ('autoRemove' in filterFn) {
    wrapped.autoRemove = filterFn.autoRemove;
  }
  return wrapped;
};

const baseCustomFilterFns: Record<string, FilterFn<any>> = {
  includesOption: (row, columnId, filterValue) => {
    if (!filterValue || !Array.isArray(filterValue) || filterValue.length === 0)
      return true;

    const rawCellValue = row.renderValue(columnId);
    const cellValues = (
      Array.isArray(rawCellValue) ? rawCellValue : [rawCellValue]
    ).map((v) => (v?.id ?? v)?.toString());

    return filterValue.some((option: any) =>
      cellValues.includes((option?.id ?? option)?.toString()),
    );
  },
  formattedDate: (row, columnId, filterValue) => {
    const value = row.renderValue(columnId);

    if (
      !Array.isArray(filterValue) ||
      (value !== undefined && value !== null && typeof value !== 'string')
    ) {
      return true;
    }

    const cellDate = getDayjsFromAny(value)?.toDate();
    const cellFrom = cellDate || new Date(MIN_TIMESTAMP);
    const cellTo = cellDate || new Date(MAX_TIMESTAMP);

    const filterFrom = new Date(
      getIsoDateFromFormattedDate(filterValue[0]) || MIN_TIMESTAMP,
    );
    const filterTo = new Date(
      getIsoDateFromFormattedDate(filterValue[1]) || MAX_TIMESTAMP,
    );

    return Boolean(filterFrom <= cellFrom && filterTo >= cellTo);
  },
  formattedDateTime: (row, columnId, filterValue) => {
    const value = row.renderValue(columnId);

    if (
      !Array.isArray(filterValue) ||
      (value !== undefined && value !== null && typeof value !== 'string')
    ) {
      return true;
    }

    const cellDate = getDayjsFromAny(value)?.toDate();
    const cellFrom = cellDate || new Date(MIN_TIMESTAMP);
    const cellTo = cellDate || new Date(MAX_TIMESTAMP);

    const filterFrom = new Date(
      getIsoDateFromFormattedDateTime(filterValue[0]) || MIN_TIMESTAMP,
    );
    const filterTo = new Date(
      getIsoDateFromFormattedDateTime(filterValue[1]) || MAX_TIMESTAMP,
    );

    return Boolean(filterFrom <= cellFrom && filterTo >= cellTo);
  },
  validPeriod: (row, columnId, filterValue) => {
    const value:
      (string & { formValue: [string | null, string | null] }) | undefined =
      row.renderValue(columnId);
    const formValue = value?.formValue;

    if (!Array.isArray(formValue) || !Array.isArray(filterValue)) {
      return true;
    }

    const cellFrom =
      getDayjsFromAny(formValue[0])?.toDate() || new Date(MIN_TIMESTAMP);
    const cellTo =
      getDayjsFromAny(formValue[1])?.toDate() || new Date(MAX_TIMESTAMP);

    const filterFrom = new Date(
      getIsoDateFromFormattedDate(filterValue[0]) || MIN_TIMESTAMP,
    );
    const filterTo = new Date(
      getIsoDateFromFormattedDate(filterValue[1]) || MAX_TIMESTAMP,
    );

    return Boolean(filterFrom <= cellTo && filterTo >= cellFrom);
  },
  booleanSelect: (row, columnId, filterValue) =>
    filterValue === null || filterValue === row.renderValue(columnId),
  select: (row, columnId, filterValue) => {
    if (filterValue == null || filterValue === '') return true;
    const value = row.getValue(columnId);
    return value?.toString() === filterValue.toString();
  },
};

export const filterFns: Record<string, FilterFn<any>> = Object.entries(
  baseCustomFilterFns,
).reduce<Record<string, FilterFn<any>>>((acc, [name, fn]) => {
  acc[name] = wrapNewCheckFilterFn(fn);
  return acc;
}, {});

export const registerFilterFn = <K extends keyof FilterFns>(
  name: K,
  filterFn: FilterFn<any>,
) => {
  if (name in filterFns) {
    throw new Error(`Filter function ${name} already registered`);
  }
  filterFns[name as string] = wrapNewCheckFilterFn(filterFn);
};

declare module '@tanstack/react-table' {
  interface FilterFns {
    includesOption: FilterFn<any>;
    formattedDate: FilterFn<any>;
    formattedDateTime: FilterFn<any>;
    validPeriod: FilterFn<any>;
    booleanSelect: FilterFn<any>;
    select: FilterFn<any>;
  }
}
